import React, { useCallback } from "react";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { formatDistanceMeters } from "../../../tools/DistanceRelated";
import {
    isMyCarLegToParkAndRide,
    resolveParkAndRideDestinationName,
} from "../../../tools/parkAndRideOsmTags.tools";
import { normalizeStopLabel } from "../../../tools/StringTools";
import { formatTime } from "../../../tools/TimeRelated";
import { getItineraryLegColorHex } from "../../../tools/TransportModeColorsTools";
import {
    getTransportModeGroupForItineraryLeg,
    TransportModeGroup,
} from "../../../tools/TransportModeGroupsTools";
import { type ItineraryLegDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import ItineraryTransportIcon from "../shared/ItineraryTransportIcon";
import {
    formatLegDepartureTimeLabel,
    getRealtimeStatusTextColor,
    isSimpleMobilityLegMode,
} from "./itineraryDetails.tools";
import ItineraryDetailsLegPill from "./ItineraryDetailsLegPill";

interface ItineraryDetailsLegTitleProps {
    "leg":        ItineraryLegDTO;
    "onLegClick": ( leg: ItineraryLegDTO ) => void;
    "isDarkUi":   boolean;
}

const VERTICAL_CONNECTOR_ICON_MODE = "dashes";

/**
 * Appends a formatted distance suffix when the leg distance is available.
 *
 * @param durationLabel Duration-based label (e.g. `"12 minutes de marche"`).
 * @param distanceMeters Optional leg distance in meters.
 * @returns Label with ` · <distance>` when distance is valid, otherwise the duration label alone.
 */
const appendDistanceSuffix = ( durationLabel: string, distanceMeters: number | undefined ): string => {
    const distanceLabel = formatDistanceMeters( distanceMeters );
    if ( distanceLabel == null ) {
        return durationLabel;
    }

    return `${ durationLabel } · ${ distanceLabel }`;
};

/**
 * Resolves the primary label for a simplified mobility leg title row.
 *
 * @param leg Itinerary leg DTO.
 * @param modeGroup Transport mode group for the leg.
 * @returns Localized title label.
 */
const resolveSimpleMobilityLegLabel = (
    leg: ItineraryLegDTO,
    modeGroup: TransportModeGroup,
): string => {
    if ( modeGroup === TransportModeGroup.WALK ) {
        return appendDistanceSuffix( `${ formatTime( leg.duration ) } de marche`, leg.distance );
    }

    if ( modeGroup === TransportModeGroup.BICYCLE ) {
        return appendDistanceSuffix( `${ formatTime( leg.duration ) } de vélo`, leg.distance );
    }

    return normalizeStopLabel( leg.from?.name ) ?? "Départ";
};

/**
 * Resolves the destination label for a mycar leg ending at a park-and-ride facility.
 *
 * @param leg Itinerary leg DTO.
 * @returns Normalized destination label, or `null` when unavailable.
 */
const resolveParkAndRideLegDestinationLabel = ( leg: ItineraryLegDTO ): string | null => {
    const parkAndRideName = resolveParkAndRideDestinationName( leg.to );
    if ( parkAndRideName == null ) {
        return null;
    }

    return normalizeStopLabel( parkAndRideName ) ?? parkAndRideName;
};

const ItineraryDetailsLegTitle: React.FC<ItineraryDetailsLegTitleProps> = ( {
    leg,
    onLegClick,
    isDarkUi,
} ) => {
    const modeGroup = getTransportModeGroupForItineraryLeg( leg );
    const palette = getUIColorPalette( isDarkUi );
    const connectorColor = getItineraryLegColorHex( leg );
    const parkAndRideDestinationLabel = isMyCarLegToParkAndRide( leg )
        ? resolveParkAndRideLegDestinationLabel( leg )
        : null;

    const handleClick = useCallback( ( event: React.MouseEvent<HTMLDivElement> ): void => {
        event.stopPropagation();
        onLegClick( leg );
    }, [ leg, onLegClick ] );

    const buildDepartureTimeContent = ( alignRight: boolean ): React.ReactNode => {
        if ( leg.from == null ) {
            return null;
        }

        const timeAlignmentClassName = alignRight ? "ml-auto mr-1 text-right" : "text-left";

        if ( leg.from.time === leg.from.scheduledTime ) {
            return (
                <span
                    className={`${ timeAlignmentClassName } text-xs font-semibold`}
                    style={{ "color": palette.overlayTextSecondary }}
                >
                    {formatLegDepartureTimeLabel( leg.from.time, modeGroup )}
                </span>
            );
        }

        const realtimeTextColor = getRealtimeStatusTextColor( isDarkUi, leg.from.time, leg.from.scheduledTime );
        const realtimeAlignmentClassName = alignRight
            ? "ml-auto flex flex-col items-end"
            : "flex flex-col items-start";

        return (
            <span className={realtimeAlignmentClassName}>
                <span
                    className={`${ alignRight ? "mr-1 text-right" : "text-left" } text-xs font-semibold`}
                    style={{ "color": realtimeTextColor }}
                >
                    {formatLegDepartureTimeLabel( leg.from.time, modeGroup )}
                </span>
                <span
                    className={`${ alignRight ? "mr-1 text-right" : "text-left" } text-xs font-semibold line-through`}
                    style={{ "color": palette.overlayTextSecondary }}
                >
                    {formatLegDepartureTimeLabel( leg.from.scheduledTime, modeGroup )}
                </span>
            </span>
        );
    };

    if ( isSimpleMobilityLegMode( leg.mode ) ) {
        return (
            <div
                className="cursor-pointer"
                id={`itinerary-details-leg-${ leg.id }-title`}
                onClick={handleClick}
            >
                <div className="mb-0 flex items-center">
                    <div className="ml-1 mr-4 flex w-4 items-center justify-center">
                        {ItineraryTransportIcon( leg.mode, true, "plain", leg.rental?.vehicle?.type, leg )}
                    </div>
                    <div className="flex h-full w-full min-w-0 items-center">
                        {parkAndRideDestinationLabel != null
                            ? (
                                <>
                                    <ItineraryDetailsLegPill leg={leg} />
                                    <span
                                        aria-label={`vers ${ parkAndRideDestinationLabel }`}
                                        className="block min-w-0 flex-1 truncate text-ellipsis text-xs font-semibold mr-2"
                                        style={{ "color": palette.overlayTextSecondary }}
                                    >
                                        {parkAndRideDestinationLabel}
                                    </span>
                                </>
                            )
                            : (
                                <>
                                    <div className="mr-[0.80rem] flex w-[3px] shrink-0 items-start justify-center self-stretch overflow-visible">
                                        {ItineraryTransportIcon(
                                            VERTICAL_CONNECTOR_ICON_MODE,
                                            false,
                                            "plain",
                                            undefined,
                                            undefined,
                                            connectorColor,
                                        )}
                                    </div>
                                    <span
                                        className="min-w-0 flex-1 truncate text-ellipsis text-xs font-semibold mr-2"
                                        style={{ "color": palette.overlayTextSecondary }}
                                    >
                                        {resolveSimpleMobilityLegLabel( leg, modeGroup )}
                                    </span>
                                </>
                            )}
                        {modeGroup !== TransportModeGroup.WALK && modeGroup !== TransportModeGroup.BICYCLE
                            ? buildDepartureTimeContent( true )
                            : null}
                    </div>
                </div>
            </div>
        );
    }

    const mainLabelContent = normalizeStopLabel( leg.from?.name ) ?? "Départ";

    const rightContent = leg.from
        ? buildDepartureTimeContent( true )
        : <></>;

    return (
        <div
            className="cursor-pointer"
            id={`itinerary-details-leg-${ leg.id }-title`}
            onClick={handleClick}
        >
            <div className="mb-0 flex items-center">
                <div className="ml-1 mr-4 flex w-4 items-center justify-center">
                    {ItineraryTransportIcon( leg.mode, true, "plain", leg.rental?.vehicle?.type, leg )}
                </div>
                <div className="flex h-full w-full min-w-0 items-center">
                    <ItineraryDetailsLegPill leg={leg} />
                    <span
                        className="block min-w-0 flex-1 truncate text-ellipsis text-xs font-semibold mr-2"
                        style={{ "color": palette.overlayTextSecondary }}
                    >
                        {mainLabelContent}
                    </span>
                    {rightContent}
                </div>
            </div>
        </div>
    );
};

export default ItineraryDetailsLegTitle;
